import {
    JsonDescription,
    JsonWidgetDataAvailability,
    mapDataAvailability,
    mapDescription,
} from 'adapter/out/client/widgets/data/mappers/common';
import { WidgetType } from 'domain/entity/widget';
import { ColorType } from 'domain/entity/widgetData';
import TableData, {
    CommonContent,
    Grid,
    GridCell,
    OneOfAllSimpleContent,
    OneOfAllSimpleContentMinusTag,
    Row,
    SortableTableCell,
    TableCellType,
    TableColumn,
    TableFooter,
} from 'domain/entity/widgets/TableData';

export const asTableWidget = (json: JsonTable) =>
    new TableData(mapDataAvailability(json.data_availability), mapDescription(json.description), mapColumns(json.data.columns));

const mapColumns = (columns: JsonTableColumn[]): TableColumn[] =>
    columns.map((column) => ({
        headerTitle: column.header_title,
        fixed: column.fixed,
        cells: mapSortableTableCells(column.cells),
        footer: column.footer ? mapTableFooter(column.footer) : null,
        widthProportion: column.width_proportion,
        isSortable: column.is_sortable === undefined ? true : column.is_sortable,
    }));

const mapSortableTableCells = (cells: JsonSortableTableCell[]): SortableTableCell[] =>
    cells.map((cell: JsonSortableTableCell) => ({
        sortValue: cell.sort_value,
        content: mapTableCellContent(cell.content),
    }));

const mapTableFooter = (footer: JsonTableFooter): TableFooter => ({ content: mapTableCellContent(footer.content) });

const isCellSimple = (cell: JsonOneOfAllSimpleContent | JsonGrid | JsonRow | JsonGridCell): cell is JsonOneOfAllSimpleContent =>
    cell.type !== TableCellType.ROW && cell.type !== TableCellType.GRID && cell.type !== TableCellType.GRID_CELL;

const mapTableCellContent = (cell: JsonOneOfAllSimpleContent | JsonGrid | JsonRow | JsonGridCell): OneOfAllSimpleContent | Grid => {
    let commonCellElements: CommonContent = {
        backgroundColorDefault: null,
        backgroundColorType: null,
        align: null,
    };
    if (isCellSimple(cell)) {
        commonCellElements = {
            backgroundColorDefault: cell.background_color_default,
            backgroundColorType: cell.background_color_type,
            align: cell.align,
        };
    }

    switch (cell.type) {
        case TableCellType.NUMERIC:
            return {
                type: TableCellType.NUMERIC,
                ...commonCellElements,
                fontColorDefault: cell.font_color_default,
                fontColorType: cell.font_color_type,
                fontWeight: cell.font_weight,
                value: cell.value,
                units: cell.units,
                tooltip: cell.tooltip,
                showSignedValue: cell.show_signed_value,
            };
        case TableCellType.DATE:
            return {
                type: TableCellType.DATE,
                ...commonCellElements,
                fontColorDefault: cell.font_color_default,
                fontColorType: cell.font_color_type,
                fontWeight: cell.font_weight,
                tooltip: cell.tooltip,
                value: new Date(Date.parse(`${cell.value}+00:00`)),
            };
        case TableCellType.TEXT:
            return {
                type: TableCellType.TEXT,
                fontColorDefault: cell.font_color_default,
                fontColorType: cell.font_color_type,
                fontWeight: cell.font_weight,
                ...commonCellElements,
                tooltip: cell.tooltip,
                value: cell.value,
            };
        case TableCellType.ICON:
            return {
                type: TableCellType.ICON,
                ...commonCellElements,
                value: cell.value,
                tooltip: cell.tooltip,
                iconColorDefault: cell.icon_color_default,
                iconColorType: cell.icon_color_type,
            };
        case TableCellType.TAG:
            return {
                type: TableCellType.TAG,
                tagIcon: cell.tag_icon,
                align: cell.align,
                tagType: cell.tag_type,
                tagBackgroundColorDefault: cell.tag_background_color_default,
                tagBackgroundColorType: cell.tag_background_color_type,
                backgroundColorDefault: cell.background_color_default,
                backgroundColorType: cell.tag_background_color_type,
                content: mapTableCellContent(cell.content) as OneOfAllSimpleContentMinusTag,
            };

        case TableCellType.GRID:
            return { type: TableCellType.GRID, children: getCompositeCell(cell.children) as Row[] };
        default:
            return {
                ...commonCellElements,
                fontColorDefault: null,
                fontColorType: null,
                tooltip: null,
                fontWeight: null,
                type: TableCellType.TEXT,
                value: '',
            };
    }
};

const getCompositeCell = (children: (JsonGridCell | JsonRow | JsonGrid)[]): (Row | Grid | GridCell)[] =>
    children.map((child: JsonGridCell | JsonRow | JsonGrid): Row | Grid | GridCell => {
        if (child.type === TableCellType.ROW) {
            return {
                type: child.type,
                children: getCompositeCell(child.children),
            };
        }
        if (child.type === TableCellType.GRID) {
            return {
                type: child.type,
                children: getCompositeCell(child.children) as Row[],
            };
        }
        return {
            type: child.type,
            xs: child.xs,
            sm: child.sm,
            md: child.md,
            lg: child.lg,
            xsOffset: child.xs_offset,
            smOffset: child.sm_offset,
            mdOffset: child.md_offset,
            lgOffset: child.lg_offset,
            content: mapTableCellContent(child.content),
        };
    });

export type JsonTable = {
    type: WidgetType;
    description: JsonDescription;
    data: { columns: JsonTableColumn[] };
    ['data_availability']: JsonWidgetDataAvailability;
};

// FIRST LEVEL TYPES
export type JsonTableColumn = {
    ['header_title']: string;
    fixed: boolean;
    cells: JsonSortableTableCell[];
    footer: JsonTableFooter | null;
    ['width_proportion']: number;
    ['is_sortable']?: boolean;
};

export type JsonSortableTableCell = {
    ['sort_value']: number;
    content: JsonOneOfAllSimpleContent | JsonGrid;
};

export type JsonTableFooter = {
    content: JsonOneOfAllSimpleContent | JsonGrid;
};

// FUNDAMENTAL ELEMENTS
export type JsonSimpleTableCell = {
    content: JsonOneOfAllSimpleContent;
};

export type JsonGrid = {
    type: TableCellType.GRID;
    children: JsonRow[];
};

export type JsonRow = {
    type: TableCellType.ROW;
    children: (JsonRow | JsonGridCell | JsonGrid)[];
};

export type JsonGridCell = {
    type: TableCellType.GRID_CELL;
    content: JsonOneOfAllSimpleContent | JsonGrid;
    xs: number | null;
    sm: number | null;
    md: number | null;
    lg: number | null;
    ['xs_offset']: number | null;
    ['sm_offset']: number | null;
    ['md_offset']: number | null;
    ['lg_offset']: number | null;
};

// CELL CONTENTS

export type JsonCommonContent = {
    ['background_color_type']: ColorType | null;
    ['background_color_default']: string | null;
    align: string | null;
    tooltip: string | null;
};

export type JsonTextContent = {
    type: TableCellType.TEXT;
    value: string | null;
    ['font_color_type']: ColorType | null;
    ['font_color_default']: string | null;
    ['font_weight']: number | null;
} & JsonCommonContent;

export type JsonDateContent = {
    type: TableCellType.DATE;
    value: Date | null;
    ['font_color_type']: ColorType | null;
    ['font_color_default']: string | null;
    ['font_weight']: number | null;
} & JsonCommonContent;

export type JsonNumericContent = {
    type: TableCellType.NUMERIC;
    units: string;
    value: number | null;
    ['show_signed_value']: boolean;
    ['font_color_type']: ColorType | null;
    ['font_color_default']: string | null;
    ['font_weight']: number | null;
} & JsonCommonContent;

export type JsonIconContent = {
    type: TableCellType.ICON;
    value: string;
    ['icon_color_type']: ColorType | null;
    ['icon_color_default']: string | null;
} & JsonCommonContent;

export type JsonTagCell = {
    type: TableCellType.TAG;
    content: JsonOneOfAllSimpleContent;
    ['tag_background_color_type']: ColorType | null;
    ['tag_background_color_default']: string | null;
    ['tag_icon']: string | null;
    ['tag_type']: string | null;
} & JsonCommonContent;

export type JsonOneOfAllSimpleContent = JsonTextContent | JsonIconContent | JsonNumericContent | JsonDateContent | JsonTagCell;

export type AllCellComponents = JsonSimpleTableCell | JsonGrid | JsonRow | JsonGridCell;
